import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, timer             } from 'rxjs';
import { map, switchMap, takeUntil, tap, repeat } from 'rxjs/operators';

import { SessionStorageService, QueryCollectorService, HelperService } from '@shared/services';
import { InvoiceModel, MetaPagingModel, ParsedFileModel              } from '@shared/models';
import { Invoice                                                     } from '@shared/factories';

import { environment } from 'environments/environment';

interface InvoiceListResponseModel    { customer_invoices: InvoiceModel[]; meta: MetaPagingModel; }
interface InvoiceDetailsResponseModel { customer_invoice:  InvoiceModel;                          }

@Injectable({
  providedIn: 'root'
})

export class InvoicesService {
  private iType: string = 'invoices';
          start$ = new Subject<void>();
  private stop$  = new Subject<void>();

  private get INVOICES_API():           string { return `${environment.apiUrl}api/portal/v3/customer_invoices`; };
  private get INVOICES_DASHBOARD_API(): string { return `${this.INVOICES_API}/dashboard`;                       };
  private get INVOICES_ARCHIVE_API():   string { return `${this.INVOICES_API}/archive`;                         };

  private SALARY_REPORT_API(externalId, invoiceId): string { return `${environment.apiUrl}/api/portal/v3/external_employees/${externalId}/profile/salary_reports/${invoiceId}/document`; };

  constructor (
    private http:                  HttpClient,
    private helperService:         HelperService,
    private sessionStorageService: SessionStorageService,
    private queryCollectorService: QueryCollectorService
  ) { }

  forceReload(iType: string = null): void {
    this.iType = iType || this.iType;
    this.forceStop();
    this.start$.next();
  }

  forceStop(reset: boolean = null): void {
    if (reset) this.iType = 'invoices';
    this.stop$.next();
  }

  get getInvoices(): Observable<Invoice[]> {
    return timer(0).pipe(
      switchMap(() => this.requestInvoices(this.iType)),
      takeUntil(this.stop$),
      repeat({ delay: () => this.start$ })
    );
  }

  private requestInvoices(iType: string): Observable<Invoice[]> {
    return this.http.get<InvoiceListResponseModel>(this.getInovicesUrl(iType)).pipe(
      tap(res => {
        this.sessionStorageService.changeTotalCount(res.meta.paging.total_count);
        this.sessionStorageService.changeTotalPages(res.meta.paging.total_pages);
      }),
      map(res => res.customer_invoices.map(item => new Invoice(item)))
    );
  }

  private getInovicesUrl(iType: string): string {
    if      (iType === 'invoices')         return this.INVOICES_DASHBOARD_API + this.queryCollectorService.getInvoicesQuery();
    else if (iType === 'invoices-archive') return this.INVOICES_ARCHIVE_API   + this.queryCollectorService.getInvoicesArchiveQuery();
  }

  archiveInvoice(  invoiceId: number): Observable<Invoice> { return this.changeInvoiceArchiveStatus(invoiceId, true);  }
  unArchiveInvoice(invoiceId: number): Observable<Invoice> { return this.changeInvoiceArchiveStatus(invoiceId, false); }

  getSalaryReportPDF(externalId: number, invoiceId: number): Observable<ParsedFileModel> {
    return this.helperService.loadFileAsBlobByUrl(this.SALARY_REPORT_API(externalId, invoiceId), 'application/pdf');
  }  

  private changeInvoiceArchiveStatus(invoiceId: number, archived): Observable<Invoice> {
    let body = { customer_invoice: { archived } };
    return this.http.put<InvoiceDetailsResponseModel>(`${this.INVOICES_API}/${invoiceId}`, body).pipe(
      map(res => new Invoice(res.customer_invoice))
    );
  }

  readInvoice(invoiceId: number): Observable<Invoice> {
    return this.http.put<InvoiceDetailsResponseModel>(`${this.INVOICES_API}/${invoiceId}/read`, {}).pipe(
      map(res => new Invoice(res.customer_invoice))
    );
  }

  getDocumnetUrlByType(invoice: Invoice, type: string): string {
    let documentType = type === 'pdf' ? 'document'           :
                       type === 'xml' ? 'electronic_invoice' : null;
    if (documentType) return `${this.INVOICES_API}/${invoice.id}/${documentType}`;
    return null;
  }

}
